import { SystemLanguage } from './../../../../user/model/user.model';
import { FormArray, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { SubtitlesControlsModel } from '../../models';
import { UserService } from '@app/user';
import { SubtitlesService } from './services/subtitles.service';
import { Subscription, startWith, tap } from 'rxjs';

@Component({
    selector: 'app-upload-subtitles-list',
    templateUrl: './subtitles-list.component.html',
    styleUrls: ['./subtitles-list.component.scss'],
    providers: [SubtitlesService],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class UploadSubtitlesListComponent implements OnInit, OnDestroy {
    @Input({ required: true }) subtitles!: FormArray<FormGroup<SubtitlesControlsModel>>;
    @Output() validatingEvent = new EventEmitter<boolean>();

    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly subtitlesService = inject(SubtitlesService);
    private readonly userService = inject(UserService);
    private readonly subscriptions = new Subscription();

    get canAddNewSubtitles() {
        const subtitlesLength = this.subtitles.length ? this.subtitles.getRawValue().filter((sub) => sub.enabled === undefined || sub.enabled)?.length : 0;
        return (this.userService.user?.company.enabledLanguages.length ?? 0) > subtitlesLength;
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.subtitles.valueChanges
                .pipe(
                    startWith(this.subtitles.getRawValue()),
                    tap((values) => {
                        this.subtitlesService.setLangs(
                            values
                                .map((value) => {
                                    if (value.language === value.newLanguage) return value.language;

                                    return value.newLanguage;
                                })
                                .filter((lang): lang is SystemLanguage | undefined => lang !== null),
                        );
                        this.validatingEvent.emit(this.validateSubtitles);
                    }),
                )
                .subscribe(),
        );

        this.subscriptions.add(
            this.subtitlesService.validating$
                .pipe(
                    tap((validating) => {
                        if (validating) {
                            this.validatingEvent.emit(validating);
                        } else if (!this.validateSubtitles) {
                            this.validatingEvent.emit(validating);
                        }
                    }),
                )
                .subscribe(),
        );
    }

    get validateSubtitles() {
        const subtitles = this.subtitles.getRawValue();
        const savedLanguagesList = subtitles.map((sub) => sub.language);
        const hasSameLang = subtitles.find((sub) => savedLanguagesList.includes(<SystemLanguage>sub.newLanguage) && sub.language !== sub.newLanguage);
        if (hasSameLang) {
            return true;
        }

        const hasSubtitlesWithoutFile = subtitles.find((sub) => sub.filename?.length === 0 && sub.hash?.length === 0 && sub.newLanguage);
        if (hasSubtitlesWithoutFile) {
            return true;
        }

        const hasFileWithoutSubtitles = subtitles.find((sub) => (sub.filename?.length ?? 0) > 0 && (sub.hash?.length ?? 0) > 0 && !sub.newLanguage);
        if (hasFileWithoutSubtitles) {
            return true;
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    addNewSubtitles() {
        const subtitles = this.formBuilder.group<SubtitlesControlsModel>({
            language: this.formBuilder.control(null),
            downloadUrl: this.formBuilder.control(''),
            filename: this.formBuilder.control(''),
            hash: this.formBuilder.control(''),
            newLanguage: this.formBuilder.control(null),
        });

        this.subtitles.push(subtitles);
    }

    removeSubtitles(index: number) {
        this.subtitles.removeAt(index);
    }
}
